/**
 * Auth form + API response schemas (zod).
 * The form schemas are the source of truth for both validation and TS types.
 */

import { z } from 'zod';

/* ─── Form schemas (for React Hook Form) ─────────────────────────── */

export const loginFormSchema = z.object({
  email: z.string().email('Please enter a valid email address.').max(254),
  password: z.string().min(1, 'Password is required.'),
});

export type LoginFormValues = z.infer<typeof loginFormSchema>;

export const signupFormSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required.').max(200),
  last_name: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  email: z.string().email('Please enter a valid email address.').max(254),
  password: z.string().min(5, 'Password must be at least 5 characters.').max(200),
  tenantName: z.string().min(1, 'Workspace name is required.').max(200),
  department_ids: z.array(z.string()).nullable().optional(),
});

export type SignupFormValues = z.infer<typeof signupFormSchema>;

/* ─── API response schemas (validated at the lib/api boundary) ───── */

const PublicUser = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string().nullable(),
});
export type PublicUser = z.infer<typeof PublicUser>;

const PublicTenant = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
});
export type PublicTenant = z.infer<typeof PublicTenant>;

export const SignupResponseSchema = z.object({
  user: PublicUser,
  tenant: PublicTenant,
  tenantUser: z.object({ id: z.string(), tenantId: z.string() }),
  verificationRequired: z.boolean().optional(),
});
export type SignupResponse = z.infer<typeof SignupResponseSchema>;

export const LoginResponseSchema = z.object({
  user: PublicUser,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

const TenantUserSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  welcomedAt: z.string().nullable(),
  celebratedMemberIds: z.array(z.string()),
});
export type TenantUser = z.infer<typeof TenantUserSchema>;

export const MeResponseSchema = z.object({
  user: PublicUser,
  tenants: z.array(
    PublicTenant.extend({
      tenantUserId: z.string(),
    }),
  ),
  activeTenant: z
    .object({
      id: z.string(),
      tenantUserId: z.string(),
      permissions: z.array(z.string()),
    })
    .nullable(),
  tenantUser: TenantUserSchema.nullable(),
  canAddHumans: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const VerifyEmailResponseSchema = z.object({ ok: z.boolean() });
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;

export const ResendVerificationResponseSchema = z.object({ ok: z.boolean() });
export type ResendVerificationResponse = z.infer<typeof ResendVerificationResponseSchema>;
